import React from 'react';
import './Sent_gift_page.css';
import { Link } from 'react-router-dom';

import GGApi from '../../../utils/GGApi.js';
import dates from '../../../utils/dates.js';
import GG_loader from '../../general_components/GG_loader/GG_loader';
import MasterButton from '../../general_components/MasterButton/MasterButton.js';
import { Helmet } from 'react-helmet';

// Pre-fill give-a-gift from this sent gift's recipient (same pattern as Home recent gifts).
function buildSendAnotherGiftUrl(gift) {
    if (!gift || !gift.gift_for) return '/give-a-gift';
    const params = new URLSearchParams();
    params.set('Gift_for', gift.gift_for);
    if (gift.account_id) {
        params.set('account_id', String(gift.account_id));
    }
    if (gift.sent_to_email) {
        params.set('send_to_email', gift.sent_to_email);
        params.set('channel', 'email');
    } else if (gift.sent_to_number) {
        params.set('send_to_number', gift.sent_to_number);
        params.set('channel', 'text_message');
    }
    return `/give-a-gift?${params.toString()}`;
}

// Format plan datetime for display (handles ISO / MySQL datetime from API)
function formatPlanDate(raw) {
    if (!raw) return null;
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return null;
    }
}

class Sent_gift_page extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            error: null,
            gift: null,
            plan: null,
            gift_number: null,
            manage_path: null,
        };

        this.renderBadges = this.renderBadges.bind(this);
        this.renderMedia = this.renderMedia.bind(this);
        this.renderPlanSection = this.renderPlanSection.bind(this);
        this.renderSentTo = this.renderSentTo.bind(this);
    }

    componentDidMount() {
        const gift_id = this.props.match.params.gift_id;
        GGApi.get_sent_gift(gift_id)
            .then(result => {
                if (!result || !result.gift) {
                    this.setState({ loading: false, error: 'Gift not found.' });
                    return;
                }
                this.setState({
                    loading: false,
                    gift: result.gift,
                    plan: result.plan || null,
                    gift_number: result.gift_number || null,
                    manage_path: result.manage_path || null,
                });
            })
            .catch(() => {
                this.setState({ loading: false, error: 'Unable to load gift details.' });
            });
    }

    renderBadges() {
        const { gift } = this.state;
        if (!gift) return null;

        const isMonthly = !!(gift.is_monthly_gift || gift.monthly_gift_plan_id);
        const isDelivered = gift.delivered === 1 || gift.delivered === true || gift.delivered === '1';
        if (!isMonthly && !isDelivered) return null;

        return (
            <div className='sent_gift_page_badges'>
                {isMonthly ? (
                    <span className='sent_gift_page_badge'>
                        <i className='material-icons monthly_gift_badge_icon'>refresh</i>
                        Monthly Gift
                    </span>
                ) : null}
                {isDelivered ? (
                    <span className='sent_gift_page_badge delivered'>Delivered</span>
                ) : null}
            </div>
        );
    }

    renderSentTo() {
        const { gift } = this.state;
        if (gift.sent_to_email) return gift.sent_to_email;
        if (gift.sent_to_number) return gift.sent_to_number;
        return null;
    }

    renderPlanSection() {
        const { plan, gift_number, manage_path } = this.state;
        if (!plan) return null;

        const statusLabels = {
            active: 'Active',
            payment_failed_active: 'Payment issue — retrying',
            completed: 'Completed',
            canceled: 'Canceled',
            draft: 'Setting up',
            setup_failed: 'Setup failed',
        };
        const statusLabel = statusLabels[plan.status] || plan.status;

        const nextDate = formatPlanDate(plan.next_expected_charge_at);
        const giftsRemaining = Math.max(0, (plan.total_months || 0) - (plan.months_completed || 0));
        const showNextGiftDate = giftsRemaining > 0 && (
            plan.status === 'active' || plan.status === 'payment_failed_active'
        );

        return (
            <>
                <div className='two_column_text_container'>
                    <div className='two_column_line'>
                        <div className='left_column_text'><p>Status:</p></div>
                        <div className='right_column_text'><p>{statusLabel}</p></div>
                    </div>
                    {gift_number ? (
                        <div className='two_column_line'>
                            <div className='left_column_text'><p>This gift:</p></div>
                            <div className='right_column_text'>
                                <p>Gift {gift_number} of {plan.total_months}</p>
                            </div>
                        </div>
                    ) : null}
                    {showNextGiftDate ? (
                        <div className='two_column_line'>
                            <div className='left_column_text'><p>Next gift date:</p></div>
                            <div className='right_column_text'><p>{nextDate || '—'}</p></div>
                        </div>
                    ) : null}
                    {giftsRemaining > 0 ? (
                        <div className='two_column_line'>
                            <div className='left_column_text'><p>Gifts remaining:</p></div>
                            <div className='right_column_text'><p>{giftsRemaining}</p></div>
                        </div>
                    ) : null}
                </div>
                {manage_path ? (
                    <p className='sent_gift_manage_link_wrapper'>
                        <Link to={manage_path} className='green_text bold sent_gift_manage_link'>
                            Manage Monthly Gifts
                        </Link>
                    </p>
                ) : null}
            </>
        );
    }

    renderMedia() {
        const { gift } = this.state;
        if (!gift || !gift.mediaUrl) return null;
        const parts = gift.mediaUrl.split('.');
        const ext = parts[parts.length - 1].toLowerCase();
        if (ext === 'jpeg' || ext === 'jpg' || ext === 'png') {
            return <img src={gift.mediaUrl} alt='gift media' className='sent_gift_media' />;
        }
        if (ext === 'mp4' || ext === 'mov' || ext === 'qt' || ext === 'quicktime') {
            return (
                <video controls className='sent_gift_media'>
                    <source src={gift.mediaUrl} />
                </video>
            );
        }
        return null;
    }

    render() {
        const { loading, error, gift } = this.state;

        if (loading) return <GG_loader />;

        if (error || !gift) {
            return (
                <main>
                    <p className='gg_red'>{error || 'Gift not found.'}</p>
                </main>
            );
        }

        const netAmount = (
            Number(gift.gift_amount) - (1 - (gift.accepted_fees || 1)) * (Number(gift.gift_send_fee) || 0)
        ).toFixed(2);

        const isMonthly = !!(gift.is_monthly_gift || gift.monthly_gift_plan_id);
        const sentTo = this.renderSentTo();

        return (
            <div>
                <main>
                    <Helmet>
                        <title>{`Sent Gift Details | Greatest Gift`}</title>
                        <meta name='description' content='Your sent gift details.' />
                    </Helmet>

                    {this.renderBadges()}
                    <h2>Details {'&'} Greeting</h2>

                    <div className='two_column_text_container'>
                        <div className='two_column_line'>
                            <div className='left_column_text'><p>Gift for:</p></div>
                            <div className='right_column_text'><p>{gift.gift_for}</p></div>
                        </div>

                        {gift.gift_from ? (
                            <div className='two_column_line'>
                                <div className='left_column_text'><p>From:</p></div>
                                <div className='right_column_text'><p>{gift.gift_from}</p></div>
                            </div>
                        ) : null}

                        {gift.selected_goals ? (
                            <div className='two_column_line'>
                                <div className='left_column_text'><p>Chosen goals:</p></div>
                                <div className='right_column_text'>
                                    <p>{gift.selected_goals.split('; ').map(g =>
                                        g.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                                    ).join(', ')}</p>
                                </div>
                            </div>
                        ) : null}

                        <div className='two_column_line'>
                            <div className='left_column_text'><p>Gift amount:</p></div>
                            <div className='right_column_text'>
                                <p className='money'>${netAmount}</p>
                            </div>
                        </div>
                    </div>

                    {gift.greeting ? (
                        <>
                            <div className='separator'></div>
                            <p className='header_for_greeting'>Your message:</p>
                            <div className='sent_gift_greeting_box'>
                                <p>"{gift.greeting}"</p>
                            </div>
                        </>
                    ) : null}

                    {this.renderMedia()}

                    {gift.thank_you_note ? (
                        <>
                            <div className='separator'></div>
                            <p className='header_for_greeting'>Thank you note received:</p>
                            <div className='sent_gift_greeting_box'>
                                <p>"{gift.thank_you_note}"</p>
                            </div>
                            {gift.thank_you_sent_on ? (
                                <p className='small_text gray'>
                                    Received on {dates.jsDate(gift.thank_you_sent_on)}
                                </p>
                            ) : null}
                        </>
                    ) : null}

                    <div className='sent_gift_section_separator'></div>
                    <h2>Delivery Details</h2>
                    <div className='two_column_text_container'>
                        <div className='two_column_line'>
                            <div className='left_column_text'><p>Date:</p></div>
                            <div className='right_column_text'>
                                <p>{dates.jsDate(gift.delivery_date)}</p>
                            </div>
                        </div>

                        {gift.delivery_time ? (
                            <div className='two_column_line'>
                                <div className='left_column_text'><p>Time:</p></div>
                                <div className='right_column_text'>
                                    <p>{dates.deMilitarizeTime(gift.delivery_time)}</p>
                                </div>
                            </div>
                        ) : null}

                        {sentTo ? (
                            <div className='two_column_line'>
                                <div className='left_column_text'><p>Sent to:</p></div>
                                <div className='right_column_text'><p>{sentTo}</p></div>
                            </div>
                        ) : null}
                    </div>

                    {isMonthly ? (
                        <>
                            <div className='sent_gift_section_separator'></div>
                            <h2>Monthly Gift Plan</h2>
                            {this.renderPlanSection()}
                        </>
                    ) : null}

                    <MasterButton
                        buttonText='Send another gift'
                        notSticky={true}
                        links={true}
                        linkTo={buildSendAnotherGiftUrl(gift)}
                    />

                    <div className='buffer'></div>
                </main>
            </div>
        );
    }
}

export default Sent_gift_page;
